import React from 'react';
import {
	type Avatar as MediaAvatarPickerAvatar,
	AvatarPickerDialog,
} from '@atlaskit/media-avatar-picker';
import type { CroppingProperties } from '@atlassian/jira-common-rest/src/api/2/universal-avatar/type/issuetype/owner/id/types.tsx';
import OpTrigger from '@atlassian/jira-common-util-op-triggers/src/on-mount/OpTrigger.tsx';
import { useIntl } from '@atlassian/jira-intl';
import {
	toErrorId,
	toErrorMessage,
} from '@atlassian/jira-project-settings-apps-common/src/types.tsx';
import { logError } from '@atlassian/jira-project-settings-apps-common/src/util/error-logger.tsx';
import { PACKAGE_NAME } from '../model/constants.tsx';
import type { Avatar } from '../model/types.tsx';
import { getAccessibleAvatarName } from './accessible-avatar-names/index.tsx';
import messages from './messages.tsx';

export type Props = {
	isOpen: boolean;
	isSaving: boolean;
	fetchError: boolean;
	avatars: Avatar[];
	selectedAvatar: Avatar | undefined;
	/**
	 * This function is called when user chooses an avatar from the list
	 */
	onAvatarSelected: (avatar: Avatar) => void;
	onDismiss: () => void;
	onSaveImage: (image: File, properties: CroppingProperties) => void;
	onShowFetchAvatarsErrorFlag: () => void;
};

export const AvatarModal = (props: Props) => {
	const getAvatarForSelectedAvatar = (
		selectedAvatar: MediaAvatarPickerAvatar,
	): Avatar | null | undefined =>
		props.avatars.find(
			(avatar: Avatar): boolean => selectedAvatar && selectedAvatar.dataURI === avatar.uri,
		);

	const onAvatarPicked = (avatar: MediaAvatarPickerAvatar): void => {
		const newAvatar: Avatar | null | undefined = getAvatarForSelectedAvatar(avatar);
		if (newAvatar === null || newAvatar === undefined) {
			// This is a bug between the integration of Media Avatar Picker Dialog and the Project Details Avatar
			logError(
				PACKAGE_NAME,
				toErrorId('pickAvatar'),
				toErrorMessage('Attempted to save a predefined avatar but could not find it'),
			);
		} else {
			props.onAvatarSelected(newAvatar);
		}
	};

	const {
		selectedAvatar,
		avatars,
		onSaveImage,
		isSaving,
		isOpen,
		onDismiss,
		fetchError,
		onShowFetchAvatarsErrorFlag,
	} = props;

	const intl = useIntl();
	const { formatMessage } = intl;

	if (!isOpen) {
		return null;
	}

	let mediaPickerAvatars: MediaAvatarPickerAvatar[] = [];

	if (avatars) {
		mediaPickerAvatars = avatars.map((avatar) => ({
			dataURI: avatar.uri,
			name: getAccessibleAvatarName(avatar)(intl),
		}));
	}

	const selectedMediaPickerAvatar = mediaPickerAvatars.find(
		(avatar) => selectedAvatar && selectedAvatar.uri === avatar.dataURI,
	);

	return (
		<>
			<AvatarPickerDialog
				title={formatMessage(messages.modalTitle)}
				primaryButtonText={formatMessage(messages.selectAvatar)}
				predefinedAvatarsText={formatMessage(messages.defaultListTitle)}
				defaultSelectedAvatar={selectedMediaPickerAvatar}
				avatars={mediaPickerAvatars}
				onCancel={onDismiss}
				onImagePicked={onSaveImage}
				onAvatarPicked={onAvatarPicked}
				isLoading={isSaving}
			/>
			{fetchError === true && <OpTrigger op={onShowFetchAvatarsErrorFlag} />}
		</>
	);
};
